import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { CheckCircle2, Calendar, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import wtaLogo from "@/assets/wta-logo.png";

const CheckoutReturn = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const product = params.get("product");
  const [schedulingUrl, setSchedulingUrl] = useState<string | null>(null);
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [linkSent, setLinkSent] = useState(false);

  const isSession = product === "wta_session_60" || product === "wta_session_student";
  const isAcademy = product === "wta_academy_enrollment";

  useEffect(() => {
    if (isSession) {
      supabase.functions.invoke("calendly-link").then(({ data }) => {
        if (data?.schedulingUrl) setSchedulingUrl(data.schedulingUrl);
      });
    }
  }, [isSession]);

  const sendMagicLink = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    setSending(true);
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: { emailRedirectTo: `${window.location.origin}/members` },
    });
    setSending(false);
    if (error) toast.error(error.message);
    else {
      setLinkSent(true);
      toast.success("Check your email for a sign-in link.");
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6 py-16">
      <div className="w-full max-w-xl space-y-8 text-center">
        <img src={wtaLogo} alt="WTA" className="w-16 h-16 mx-auto object-contain" />
        <div className="flex flex-col items-center gap-3">
          <CheckCircle2 className="w-14 h-14 text-gold" />
          <h1 className="font-display text-3xl md:text-4xl font-bold text-foreground">
            Payment received
          </h1>
          <p className="text-muted-foreground font-body text-sm max-w-md">
            Thank you. A receipt has been emailed to you.
            {sessionId && <> Reference: <span className="text-xs">{sessionId.slice(-10)}</span></>}
          </p>
        </div>

        {isSession && (
          <div className="border border-gold/20 bg-card/60 backdrop-blur-md p-6 space-y-4 text-left">
            <div className="flex items-center gap-2 text-gold">
              <Calendar size={18} />
              <span className="font-body text-sm uppercase tracking-[0.2em]">Book your time</span>
            </div>
            {schedulingUrl ? (
              <>
                <p className="text-muted-foreground font-body text-sm">
                  Pick a time that works for you. You'll receive a calendar invite immediately.
                </p>
                <div className="w-full aspect-[4/5] md:aspect-video border border-gold/10">
                  <iframe
                    title="Book your session"
                    src={schedulingUrl}
                    className="w-full h-full"
                    frameBorder={0}
                  />
                </div>
                <a
                  href={schedulingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-gold font-body text-sm hover:underline"
                >
                  Open in new tab <ArrowRight size={14} />
                </a>
              </>
            ) : (
              <p className="text-muted-foreground font-body text-sm">
                Angela will email you within 24 hours to schedule your session.
              </p>
            )}
          </div>
        )}

        {isAcademy && (
          <div className="border border-gold/20 bg-card/60 backdrop-blur-md p-6 space-y-4 text-left">
            <h2 className="font-display text-lg font-semibold text-foreground">Access your members area</h2>
            <p className="text-muted-foreground font-body text-sm">
              Enter the same email you used at checkout. We'll send you a one-tap sign-in link.
            </p>
            {linkSent ? (
              <p className="text-gold font-body text-sm">
                Check your inbox — the sign-in link is on its way.
              </p>
            ) : (
              <form onSubmit={sendMagicLink} className="flex flex-col sm:flex-row gap-3">
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="flex-1 px-4 py-3 bg-secondary/50 border border-gold/20 text-foreground font-body text-sm focus:outline-none focus:border-gold/60"
                />
                <button
                  type="submit"
                  disabled={sending}
                  className="px-6 py-3 bg-gradient-gold text-primary-foreground font-body text-xs font-semibold uppercase tracking-[0.2em] disabled:opacity-50"
                >
                  {sending ? "Sending..." : "Send link"}
                </button>
              </form>
            )}
          </div>
        )}

        <Link to="/" className="inline-block text-muted-foreground hover:text-gold font-body text-sm underline">
          Return home
        </Link>
      </div>
    </div>
  );
};

export default CheckoutReturn;
